import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './config/locales/en/en.json';
import es from './config/locales/es/es.json';
import fr from './config/locales/fr/fr.json';
import de from './config/locales/de/de.json';

export const defaultNS = 'common';
const defaultLocale = 'en';
export const resources = {
	en: {
		common: en
	},
	es: {
		common: es
	},
	fr: {
		common: fr
	},
	de: {
		common: de
	}
} as const;

// i18n with default locale, then update asynchronously
i18n.use(initReactI18next).init({
	lng: defaultLocale,
	ns: [defaultNS],
	defaultNS,
	resources,
	fallbackLng: defaultLocale,
	interpolation: {
		escapeValue: false
	}
});

// extract locale from profile.locale
const extractLocaleFromProfile = (authObj: Record<string, unknown>): string | null => {
	if (authObj?.profile && typeof authObj.profile === 'object') {
		const profile = authObj.profile as Record<string, unknown>;
		if (typeof profile.locale === 'string' && profile.locale.length > 0) {
			return profile.locale;
		}
	}

	return null;
};

const getLocaleFromStorage = async (): Promise<string | null> => {
	try {
		// read sessionStorage key from config.json
		const resp = await fetch('/config/config.json', { cache: 'no-store' });
		if (!resp.ok) {
			return null;
		}

		const cfg: unknown = await resp.json();
		const cfgObj = cfg as Record<string, unknown>;
		const storageKey =
			(cfgObj?.auth && (cfgObj.auth as Record<string, unknown>)['storageKey']) ?? cfgObj['auth.storageKey'];
		if (!storageKey || typeof storageKey !== 'string') {
			return null;
		}

		const stored = sessionStorage.getItem(storageKey);
		if (!stored) {
			return null;
		}

		let authObj: unknown = null;
		try {
			authObj = JSON.parse(stored);
		} catch {
			return null;
		}

		if (!authObj || typeof authObj !== 'object') return null;
		const auth = authObj as Record<string, unknown>;

		// extract locale from profile.locale
		return extractLocaleFromProfile(auth);
	} catch {
		return null;
	}
};

const setLanguage = async () => {
	// check browser language
	const browserLang = navigator.language || navigator.languages?.[0];
	if (browserLang) {
		const short = browserLang.split(/[-_]/)[0];
		if (short in resources && i18n.language !== short) {
			await i18n.changeLanguage(short);
		}
	}

	// check language from locale in storage
	const locale = await getLocaleFromStorage();
	if (locale) {
		const short = locale.split(/[-_]/)[0];
		if (short in resources && i18n.language !== short) {
			await i18n.changeLanguage(short);
			return;
		}
	}
	// fallback english
};

// language detection
if (typeof window !== 'undefined') {
	const handleStorageChange = async () => {
		try {
			await setLanguage();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
		}
	};

	handleStorageChange();

	// on storage event check for locale change
	window.addEventListener('storage', handleStorageChange);

	// initial check of language
	let checkCount = 0;
	const maxChecks = 3;
	const checkInterval = setInterval(async () => {
		checkCount++;
		await handleStorageChange();
		if (checkCount >= maxChecks) {
			clearInterval(checkInterval);
		}
	}, 500);
}

export default i18n;
